import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import './Quiz.css';

const Quiz = () => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [results, setResults] = useState([]);
    const [score, setScore] = useState(0);
    const [userName, setUserName] = useState("");
    const [inputName, setInputName] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [certificateGenerated, setCertificateGenerated] = useState(false);
    const [generatingCert, setGeneratingCert] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const navigate = useNavigate();
    const auth = getAuth();

    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 4000);
    };

    useEffect(() => {
        onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserName(user.displayName || user.email);
                fetchQuestions();
            } else {
                navigate("/login");
            }
        });
    }, [auth, navigate]);

    const fetchQuestions = () => {
        axios.get("https://oceansense-backend.onrender.com/api/quiz/questions")
            .then(response => {
                setQuestions(response.data.questions);
                setIsLoading(false);
            })
            .catch(error => {
                console.error("Error fetching quiz questions!", error);
                setIsLoading(false);
            });
    };

    const handleChange = (e, questionId) => {
        setAnswers({
            ...answers,
            [questionId]: e.target.value
        });
    };

    const answeredCount = Object.keys(answers).length;
    const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        let calculatedScore = 0;
        const resultDetails = questions.map(question => {
            const userAnswer = answers[question.id];
            const isCorrect = userAnswer === question.correctAnswer;
            if (isCorrect) calculatedScore += 1;
            return { ...question, userAnswer: userAnswer, isCorrect: isCorrect };
        });

        setScore(calculatedScore);
        setResults(resultDetails);

        try {
            await axios.post("https://oceansense-backend.onrender.com/api/quiz/submit", {
                userName: userName,
                answers: answers,
            });
        } catch (error) {
            console.error("Error submitting quiz answers!", error);
        }
    };

    const generateCertificate = async () => {
        if (!inputName) {
            showToast("Please enter your name to generate the certificate.", "warning");
            return;
        }

        setGeneratingCert(true);
        try {
            const response = await axios.post("https://oceansense-backend.onrender.com/api/quiz/certificate", {
                userName: inputName,
            }, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Certificate_${inputName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setCertificateGenerated(true);
            showToast("Certificate downloaded successfully!", "success");
        } catch (error) {
            console.error("Error generating certificate!", error);
            showToast("Failed to generate certificate. Please try again.", "error");
        } finally {
            setGeneratingCert(false);
        }
    };

    return (
        <div className="quiz-page">
            {/* Toast */}
            {toast.show && (
                <div className={`quiz-toast ${toast.type}`}>
                    <span>{toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : '⚠️'}</span>
                    {toast.message}
                </div>
            )}

            <div className="quiz-container">
                <div className="quiz-header">
                    <span className="quiz-badge">🧠 Test Your Knowledge</span>
                    <h1>Ocean Conservation Quiz</h1>
                    <p className="quiz-subtitle">How much do you know about our oceans? Take this quiz to find out!</p>
                </div>

                {/* Progress Bar */}
                {!submitted && questions.length > 0 && (
                    <div className="quiz-progress">
                        <div className="progress-info">
                            <span>Progress</span>
                            <span>{answeredCount} / {questions.length} answered</span>
                        </div>
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="quiz-loading">
                        <div className="quiz-spinner"></div>
                        <p>Loading questions...</p>
                    </div>
                ) : questions.length === 0 ? (
                    <div className="quiz-empty">
                        <span className="empty-icon">📋</span>
                        <p>No questions available at the moment. Check back later!</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {questions.map((question, index) => (
                            <div key={question.id} className={`quiz-question ${submitted ? (results[index]?.isCorrect ? 'correct-card' : 'incorrect-card') : ''}`}>
                                <div className="question-number">Q{index + 1}</div>
                                <h2>{question.text}</h2>
                                <div className="quiz-options">
                                    {question.options.map(option => (
                                        <label key={option} className={`option-label ${answers[question.id] === option ? 'selected' : ''} ${submitted && option === question.correctAnswer ? 'correct-option' : ''} ${submitted && answers[question.id] === option && !results[index]?.isCorrect ? 'wrong-option' : ''}`}>
                                            <input
                                                type="radio"
                                                name={`question-${question.id}`}
                                                value={option}
                                                onChange={(e) => handleChange(e, question.id)}
                                                disabled={submitted}
                                            />
                                            <span className="option-radio"></span>
                                            <span className="option-text">{option}</span>
                                        </label>
                                    ))}
                                </div>
                                {submitted && (
                                    <div className={`result-badge ${results[index]?.isCorrect ? 'correct' : 'incorrect'}`}>
                                        {results[index]?.isCorrect ? '✅ Correct!' : `❌ Correct: ${results[index]?.correctAnswer}`}
                                    </div>
                                )}
                            </div>
                        ))}

                        {!submitted && (
                            <button type="submit" className="submit-quiz-btn" disabled={answeredCount < questions.length}>
                                {answeredCount < questions.length ? `Answer all questions (${questions.length - answeredCount} remaining)` : 'Submit Quiz'}
                            </button>
                        )}
                    </form>
                )}

                {submitted && (
                    <div className="score-display">
                        <div className="score-circle">
                            <span className="score-value">{score}</span>
                            <span className="score-total">/ {questions.length}</span>
                        </div>
                        <h3>{score >= (questions.length / 2) ? '🎉 Congratulations!' : '💪 Keep Learning!'}</h3>
                        <p>{score >= (questions.length / 2) ? 'Great job! You passed the quiz.' : 'Better luck next time! Review and try again.'}</p>

                        {!certificateGenerated && (
                            <div className="certificate-section">
                                <h4>📜 Download Your Certificate</h4>
                                <div className="certificate-form">
                                    <input
                                        type="text"
                                        className="certificate-input"
                                        placeholder="Enter your full name"
                                        value={inputName}
                                        onChange={(e) => setInputName(e.target.value)}
                                    />
                                    <button onClick={generateCertificate} className="certificate-btn" disabled={generatingCert}>
                                        {generatingCert ? '⏳ Generating...' : '📜 Download Certificate'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {certificateGenerated && (
                            <div className="certificate-success">
                                ✅ Certificate generated for <strong>{inputName}</strong>!
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Certificate Generating Overlay */}
            {generatingCert && (
                <div className="cert-overlay">
                    <div className="cert-overlay-card">
                        <div className="cert-spinner"></div>
                        <h3>Generating Your Certificate</h3>
                        <p>Please wait while we prepare your certificate. This may take a moment as our server processes your request...</p>
                        <div className="cert-progress">
                            <div className="cert-progress-bar"></div>
                        </div>
                        <span className="cert-hint">Do not close this page</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Quiz;
